import React, {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { flatten } from 'ramda'
import styled, { css } from 'styled-components'
import { isFalsy } from 'ramda-adjunct'
import Linkify from 'react-linkify'
import MUIDoneIcon from '@material-ui/icons/Done'
import MUIDoneAllIcon from '@material-ui/icons/DoneAll'
import MUIAccessTimeIcon from '@material-ui/icons/AccessTime'
import MUIRefreshIcon from '@material-ui/icons/Refresh'
import { useIntl } from 'gatsby-plugin-intl'
import moment from 'moment'
import ExampleUserAvatarUrl from 'src/assets/avatar-placeholder.svg'
import { API_STATES } from 'src/ducks/consts'
import { formatMessagesGroupTimestamp } from 'src/utils/helpers'
import debounce from 'lodash.debounce'
import MessageTimestampTooltip from './MessageTimestampTooltip'
import { hasServerMessageId, isUnreadIncomingMessage } from '../duck/helpers'

const useBrowserLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect

const messageIdentity = message => {
  if (message?.clientId) return `client:${message.clientId}`
  if (message?.id != null) return `id:${message.id}`
  return `message:${message?.createdAt}|${message?.text}|${message?.isInterlocutorMessage}`
}

const sameSender = (left, right) =>
  Boolean(
    left &&
      right &&
      !left.isSystemMessage &&
      !right.isSystemMessage &&
      left.isInterlocutorMessage === right.isInterlocutorMessage
  )

const isScrolledToBottom = container =>
  container.scrollHeight - container.clientHeight - container.scrollTop <= 2

const MessagesList = ({
  className,
  lastRequest,
  messages,
  messagesState,
  newMessagesLabel,
  markMessageAsSeen,
  canRead,
  scrollPosition,
  onScrollPositionChange,
  onRetry,
  getMessages,
  conversationId,
  messagesRef,
  endOfChatMessage
}) => {
  const unreadMarkerRef = useRef(null)
  const initialPositionedRef = useRef(false)
  const paginationRef = useRef(null)
  const isAtBottomRef = useRef(true)
  const lastScrollTopRef = useRef(0)
  const previousNewestKeyRef = useRef(null)
  const savedPositionRef = useRef(scrollPosition)
  const scrollToUnreadRef = useRef(false)
  const scrollCallbackRef = useRef(onScrollPositionChange)
  scrollCallbackRef.current = onScrollPositionChange
  const getMessagesRef = useRef(getMessages)
  getMessagesRef.current = getMessages
  const [unreadStartKey, setUnreadStartKey] = useState(null)
  const [hasOverflow, setHasOverflow] = useState(false)
  const [showNewMessagesButton, setShowNewMessagesButton] = useState(false)
  const flatMessages = useMemo(() => flatten(messages || []), [messages])
  const { latestReadMessageKey, messagesBeforeRead } = useMemo(() => {
    const latestReadIndex = flatMessages.reduce(
      (latestIndex, message, index) => {
        const isReadOutgoingMessage =
          !message.isInterlocutorMessage &&
          !message.isSystemMessage &&
          message.seenAt &&
          (message.sendStatus || 'sent') === 'sent'
        return isReadOutgoingMessage ? index : latestIndex
      },
      -1
    )
    return {
      latestReadMessageKey:
        latestReadIndex < 0
          ? null
          : messageIdentity(flatMessages[latestReadIndex]),
      messagesBeforeRead: new Set(
        flatMessages.slice(0, Math.max(latestReadIndex, 0)).map(messageIdentity)
      )
    }
  }, [flatMessages])
  const oldestMessage = flatMessages[0]
  const newestMessage = flatMessages[flatMessages.length - 1]
  const oldestKey = oldestMessage && messageIdentity(oldestMessage)
  const newestKey = newestMessage && messageIdentity(newestMessage)

  const persistPosition = useMemo(
    () =>
      debounce(position => {
        if (scrollCallbackRef.current) scrollCallbackRef.current(position)
      }, 100),
    []
  )

  const savePosition = useCallback(
    container => {
      const rootTop = container.getBoundingClientRect().top
      const anchor = Array.from(
        container.querySelectorAll('[data-message-id]')
      ).find(node => node.getBoundingClientRect().bottom > rootTop)
      persistPosition({
        top: container.scrollTop,
        nearBottom: isScrolledToBottom(container),
        anchorId: anchor?.getAttribute('data-message-id') || null,
        anchorOffset: anchor ? anchor.getBoundingClientRect().top - rootTop : 0
      })
    },
    [persistPosition]
  )

  const updateNewMessagesButton = useCallback(container => {
    const marker = unreadMarkerRef.current
    setShowNewMessagesButton(
      Boolean(
        container &&
          marker &&
          marker.getBoundingClientRect().top >=
            container.getBoundingClientRect().bottom
      )
    )
  }, [])

  const scrollToNewMessages = useCallback(
    (behavior = 'auto') => {
      const container = messagesRef.current?.parentElement
      const marker = unreadMarkerRef.current
      if (!container || !marker) return

      const distanceFromTop =
        marker.getBoundingClientRect().top -
        container.getBoundingClientRect().top
      const reduceMotion =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      container.scrollTo({
        top: container.scrollTop + distanceFromTop - 8,
        behavior: reduceMotion ? 'auto' : behavior
      })
      lastScrollTopRef.current = container.scrollTop
      isAtBottomRef.current = isScrolledToBottom(container)
      savePosition(container)
      updateNewMessagesButton(container)
    },
    [messagesRef, savePosition, updateNewMessagesButton]
  )

  useEffect(
    () => () => {
      persistPosition.flush()
      persistPosition.cancel()
    },
    [persistPosition]
  )

  useBrowserLayoutEffect(() => {
    const container = messagesRef.current?.parentElement
    if (!container || messagesState !== API_STATES.DONE) return

    const wasPaginating = Boolean(paginationRef.current)
    if (paginationRef.current) {
      const {
        height,
        top,
        oldestKey: previousOldestKey
      } = paginationRef.current
      if (oldestKey !== previousOldestKey) {
        container.scrollTop = top + container.scrollHeight - height
      }
      lastScrollTopRef.current = container.scrollTop
      isAtBottomRef.current = isScrolledToBottom(container)
      paginationRef.current = null
      savePosition(container)
    }

    if (!initialPositionedRef.current) {
      initialPositionedRef.current = true
      previousNewestKeyRef.current = newestKey
      const firstUnread = flatMessages.find(isUnreadIncomingMessage)
      const saved = savedPositionRef.current
      scrollToUnreadRef.current = Boolean(
        firstUnread && (!saved || saved.nearBottom)
      )

      if (firstUnread) {
        setUnreadStartKey(messageIdentity(firstUnread))
      }
      if (saved && !saved.nearBottom) {
        container.scrollTop = saved.top
        const anchor = Array.from(
          container.querySelectorAll('[data-message-id]')
        ).find(node => node.getAttribute('data-message-id') === saved.anchorId)
        if (anchor) {
          container.scrollTop +=
            anchor.getBoundingClientRect().top -
            container.getBoundingClientRect().top -
            saved.anchorOffset
        }
        isAtBottomRef.current = isScrolledToBottom(container)
        lastScrollTopRef.current = container.scrollTop
      } else if (!firstUnread) {
        container.scrollTop = container.scrollHeight
        lastScrollTopRef.current = container.scrollTop
      }
      savePosition(container)
      return
    }

    if (newestKey !== previousNewestKeyRef.current) {
      if (isAtBottomRef.current && !wasPaginating) {
        container.scrollTop = container.scrollHeight
        lastScrollTopRef.current = container.scrollTop
        savePosition(container)
      } else {
        const previousNewestIndex = flatMessages.findIndex(
          message => messageIdentity(message) === previousNewestKeyRef.current
        )
        const firstNewUnread = flatMessages
          .slice(previousNewestIndex + 1)
          .find(isUnreadIncomingMessage)
        if (firstNewUnread) {
          const previousMessages = flatMessages.slice(
            0,
            previousNewestIndex + 1
          )
          const previousMarkerIndex = previousMessages.findIndex(
            message => messageIdentity(message) === unreadStartKey
          )
          const hasUnreadInPreviousBatch = previousMessages
            .slice(Math.max(previousMarkerIndex, 0))
            .some(isUnreadIncomingMessage)
          if (
            !unreadStartKey ||
            previousMarkerIndex < 0 ||
            !hasUnreadInPreviousBatch
          ) {
            setUnreadStartKey(
              messageIdentity(
                hasUnreadInPreviousBatch
                  ? flatMessages.find(isUnreadIncomingMessage)
                  : firstNewUnread
              )
            )
          }
        }
      }
      previousNewestKeyRef.current = newestKey
    }
  }, [messages, messagesState, oldestKey, newestKey, savePosition])

  useBrowserLayoutEffect(() => {
    if (
      !scrollToUnreadRef.current ||
      !unreadStartKey ||
      !unreadMarkerRef.current
    ) {
      return
    }
    scrollToUnreadRef.current = false
    scrollToNewMessages()
  }, [unreadStartKey, scrollToNewMessages])

  useBrowserLayoutEffect(() => {
    const container = messagesRef.current?.parentElement
    if (!container) {
      setHasOverflow(false)
      setShowNewMessagesButton(false)
      return undefined
    }

    const updateLayout = () => {
      setHasOverflow(
        Boolean(
          endOfChatMessage &&
            container.scrollHeight > container.clientHeight + 1
        )
      )
      updateNewMessagesButton(container)
    }
    updateLayout()
    window.addEventListener('resize', updateLayout)
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateLayout)
        : null
    if (observer) {
      observer.observe(container)
      observer.observe(messagesRef.current)
    }
    return () => {
      window.removeEventListener('resize', updateLayout)
      if (observer) observer.disconnect()
    }
  }, [messages, unreadStartKey, endOfChatMessage, updateNewMessagesButton])

  const debouncedGetMessages = useMemo(
    () =>
      debounce(element => {
        if (
          paginationRef.current ||
          messagesState === API_STATES.IN_PROGRESS ||
          !lastRequest?.[conversationId]?.length ||
          !oldestMessage
        ) {
          return
        }

        paginationRef.current = {
          height: element.scrollHeight,
          top: element.scrollTop,
          oldestKey
        }
        getMessagesRef.current({
          conversationId,
          values: { limit: 5, maxSentDate: oldestMessage.createdAt }
        })
      }, 200),
    [conversationId, lastRequest, messagesState, oldestKey]
  )

  useEffect(() => () => debouncedGetMessages.cancel(), [debouncedGetMessages])

  const handleScroll = event => {
    const element = event.target
    const previousScrollTop = lastScrollTopRef.current
    lastScrollTopRef.current = element.scrollTop
    isAtBottomRef.current = isScrolledToBottom(element)
    updateNewMessagesButton(element)
    savePosition(element)
    if (
      initialPositionedRef.current &&
      element.scrollTop <= 1 &&
      previousScrollTop > element.scrollTop
    ) {
      debouncedGetMessages(element)
    }
  }

  const scrollWithArrow = direction => {
    const container = messagesRef.current?.parentElement
    if (container) container.scrollTop += direction * 40
  }

  return (
    <div className={className}>
      <ScrollViewport
        onScroll={handleScroll}
        $hasEndOfChat={Boolean(endOfChatMessage)}
      >
        <InnerWrapper
          ref={messagesRef}
          $hasEndOfChat={Boolean(endOfChatMessage)}
        >
          {messages.map(group =>
            group.map((message, index) => {
              const messageKey = messageIdentity(message)
              const isUnreadStart =
                unreadStartKey === messageIdentity(message) &&
                !message.isSystemMessage
              const nextMessage = group[index + 1]
              const joinedAbove =
                !isUnreadStart && sameSender(group[index - 1], message)
              const joinedBelow =
                nextMessage &&
                unreadStartKey !== messageIdentity(nextMessage) &&
                sameSender(message, nextMessage)
              if (message.isSystemMessage) {
                return (
                  <Fragment key={`system-${messageKey}`}>
                    {index === 0 && (
                      <Timestamp id={`message-timestamp-system-${message.id}`}>
                        {formatMessagesGroupTimestamp(message.createdAt)}
                      </Timestamp>
                    )}
                    <SystemMessageStyled message={message.text} />
                  </Fragment>
                )
              }

              return (
                <Fragment key={`message-${messageKey}`}>
                  {index === 0 && (
                    <Timestamp id={`message-timestamp-default-${message.id}`}>
                      {formatMessagesGroupTimestamp(message.createdAt)}
                    </Timestamp>
                  )}
                  {isUnreadStart && (
                    <NewMessagesDivider
                      ref={unreadMarkerRef}
                      role='separator'
                      aria-label={newMessagesLabel}
                    >
                      <span>{newMessagesLabel}</span>
                    </NewMessagesDivider>
                  )}
                  <MessageStyled
                    isBeforeLatestRead={messagesBeforeRead.has(messageKey)}
                    showReadReceipt={
                      messageIdentity(message) === latestReadMessageKey
                    }
                    canRead={canRead}
                    messagesRef={messagesRef}
                    onRetry={() => onRetry(message)}
                    onSeen={() =>
                      markMessageAsSeen({
                        conversationId,
                        messageId: message.id
                      })
                    }
                    message={message}
                    isInterlocutorMessage={message.isInterlocutorMessage}
                    $joinedAbove={joinedAbove}
                    $joinedBelow={joinedBelow}
                  />
                </Fragment>
              )
            })
          )}
        </InnerWrapper>
      </ScrollViewport>
      {showNewMessagesButton && (
        <JumpToNewMessagesButton
          type='button'
          $hasEndOfChat={Boolean(endOfChatMessage)}
          onClick={() => scrollToNewMessages('smooth')}
        >
          {newMessagesLabel}
        </JumpToNewMessagesButton>
      )}
      {endOfChatMessage && <EndOfChat message={endOfChatMessage} />}
      {endOfChatMessage && hasOverflow && (
        <ScrollArrowControls>
          <ScrollArrowButton
            type='button'
            aria-label='Przewiń w górę'
            $direction='up'
            onClick={() => scrollWithArrow(-1)}
          />
          <ScrollArrowButton
            type='button'
            aria-label='Przewiń w dół'
            $direction='down'
            onClick={() => scrollWithArrow(1)}
          />
        </ScrollArrowControls>
      )}
    </div>
  )
}

export default styled(React.memo(MessagesList))`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
`

const ScrollViewport = styled.div`
  width: 100%;
  height: ${({ $hasEndOfChat }) =>
    $hasEndOfChat ? 'calc(100% - 0.8rem - 19px)' : '100%'};
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;

  ${({ $hasEndOfChat, theme }) =>
    $hasEndOfChat &&
    css`
      &::-webkit-scrollbar {
        width: 14px;
      }

      &::-webkit-scrollbar-button {
        display: none;
      }

      &::-webkit-scrollbar-track {
        margin-top: 15px;
        margin-bottom: 30px;
        background: ${theme.colors.white.hex()};
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.48);
        border: 3px solid ${theme.colors.white.hex()};
        border-radius: 7px;
      }
    `}
`

const ScrollArrowControls = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: calc(0.8rem + 34px);
  width: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  pointer-events: none;
  z-index: 21;
`

const ScrollArrowButton = styled.button`
  width: 14px;
  height: 15px;
  flex: 0 0 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: ${({ theme }) => theme.colors.white.hex()};
  cursor: pointer;
  pointer-events: auto;

  &:before {
    content: '';
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    ${({ $direction }) =>
      $direction === 'up'
        ? css`
            border-bottom: 7px solid #8c8c8c;
          `
        : css`
            border-top: 7px solid #8c8c8c;
          `}
  }

  &:hover:before,
  &:focus-visible:before {
    ${({ $direction }) =>
      $direction === 'up'
        ? css`
            border-bottom-color: #555;
          `
        : css`
            border-top-color: #555;
          `}
  }
`

const InnerWrapper = styled.div`
  min-height: 100%;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  padding: 0.8rem 1.2rem;
  padding-right: 28px;
  padding-bottom: ${({ $hasEndOfChat }) =>
    $hasEndOfChat ? 'calc(0.8rem + 10px)' : '0.8rem'};
`

const Avatar = styled.div`
  display: flex;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-image: url(${({ url }) => url || ExampleUserAvatarUrl});
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  margin: 0 14px 0 10px;
  flex-shrink: 0;
`

const CheckIcon = styled(MUIDoneIcon)`
  font-size: 14px !important;
  color: ${({ theme }) => theme.colors.corduroy.hex()};
`

const ReadIcon = styled(MUIDoneAllIcon)`
  font-size: 16px !important;
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
`

const MessageComponent = ({
  className,
  message,
  isInterlocutorMessage,
  $joinedAbove,
  $joinedBelow,
  onSeen,
  canRead,
  messagesRef,
  onRetry,
  showReadReceipt,
  isBeforeLatestRead
}) => {
  const intl = useIntl()
  const bubbleRef = useRef(null)
  const onSeenRef = useRef(onSeen)
  onSeenRef.current = onSeen
  const messageSeen = !isFalsy(message.seenAt)
  const canMarkAsSeen =
    isUnreadIncomingMessage(message) && hasServerMessageId(message)
  const sendStatus = message.sendStatus || 'sent'
  let deliveryKey = messageSeen ? 'read' : 'sent'
  if (sendStatus === 'pending') deliveryKey = 'sending'
  if (sendStatus === 'failed') deliveryKey = 'sendFailed'
  const deliveryLabel = intl.formatMessage({ id: `messages.${deliveryKey}` })
  const retryLabel = intl.formatMessage({ id: 'messages.retrySend' })
  const showDeliveryStatus =
    !isInterlocutorMessage &&
    (sendStatus !== 'sent' ||
      showReadReceipt ||
      (!isBeforeLatestRead && !messageSeen))
  const sentAt = moment(message.createdAt)
  const sentAtTitle = sentAt.isValid() ? sentAt.format('DD.MM.YYYY, HH:mm') : ''

  useEffect(() => {
    const node = bubbleRef.current
    const root = messagesRef.current?.parentElement
    if (!canRead || !canMarkAsSeen || !node || !root) {
      return undefined
    }
    let timer
    const isVisible = () => {
      if (document.hidden || !document.hasFocus()) return false
      const rect = node.getBoundingClientRect()
      const viewport = root.getBoundingClientRect()
      const top = Math.max(viewport.top, 0)
      const bottom = Math.min(viewport.bottom, window.innerHeight)
      const visibleHeight =
        Math.min(rect.bottom, bottom) - Math.max(rect.top, top)
      const visibleWidth =
        Math.min(rect.right, viewport.right, window.innerWidth) -
        Math.max(rect.left, viewport.left, 0)
      return (
        visibleHeight >= Math.min(rect.height * 0.75, (bottom - top) * 0.75) &&
        visibleHeight > 0 &&
        visibleWidth > 0 &&
        visibleWidth >= rect.width * 0.75
      )
    }
    const update = () => {
      if (!isVisible()) {
        clearTimeout(timer)
        timer = null
      } else if (!timer) {
        timer = setTimeout(() => {
          timer = null
          if (isVisible()) {
            onSeenRef.current()
          }
        }, 600)
      }
    }
    const observer =
      typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(update, {
            root,
            threshold: [0, 0.25, 0.5, 0.75, 1]
          })
        : null
    if (observer) observer.observe(node)
    root.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    update()
    return () => {
      clearTimeout(timer)
      if (observer) observer.disconnect()
      root.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [canRead, canMarkAsSeen, message.id, messagesRef])
  return (
    <div className={className}>
      <Avatar url={message.avatar} />
      <MessageTimestampTooltip title={sentAtTitle}>
        <TextMessage
          ref={bubbleRef}
          data-message-id={String(message.id || messageIdentity(message))}
          isInterlocutorMessage={isInterlocutorMessage}
          $sendStatus={sendStatus}
          $joinedAbove={$joinedAbove}
          $joinedBelow={$joinedBelow}
        >
          <Linkify
            componentDecorator={(decoratedHref, decoratedText, key) => (
              <a target='_blank' href={decoratedHref} key={key}>
                {decoratedText}
              </a>
            )}
          >
            {message.text}
          </Linkify>
          {showDeliveryStatus && (
            <DeliveryStatus
              data-message-status
              title={deliveryLabel}
              aria-label={deliveryLabel}
              $failed={sendStatus === 'failed'}
            >
              {sendStatus === 'pending' && (
                <MUIAccessTimeIcon
                  fontSize='inherit'
                  titleAccess={deliveryLabel}
                />
              )}
              {sendStatus === 'failed' && (
                <RetryButton
                  type='button'
                  onClick={onRetry}
                  aria-label={retryLabel}
                  title={`${deliveryLabel}. ${retryLabel}`}
                >
                  <MUIRefreshIcon fontSize='inherit' titleAccess={retryLabel} />
                </RetryButton>
              )}
              {sendStatus === 'sent' &&
                (showReadReceipt ? (
                  <ReadIcon titleAccess={deliveryLabel} />
                ) : (
                  <CheckIcon titleAccess={deliveryLabel} />
                ))}
            </DeliveryStatus>
          )}
        </TextMessage>
      </MessageTimestampTooltip>
    </div>
  )
}

const MessageStyled = styled(MessageComponent)`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: ${({ $joinedBelow }) => ($joinedBelow ? '3px' : '12px')};
  align-items: flex-end;
  ${({ $joinedBelow }) =>
    $joinedBelow &&
    css`
      ${Avatar} {
        visibility: hidden;
      }
    `}
  ${({ isInterlocutorMessage }) =>
    !isInterlocutorMessage &&
    css`
      justify-content: flex-end;
      ${Avatar} {
        display: none;
      }
    `}
`

const TextMessage = styled.div`
  display: inline-block;
  min-width: 0;
  min-height: 35px;
  border-radius: 18px;
  padding: 8px 16px;
  max-width: 85%;
  color: white;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.bluewood.hex()};
  font-size: 14px;
  position: relative;
  overflow-wrap: break-word;
  word-wrap: break-word;
  a {
    color: white;
    &:hover {
      text-decoration: underline;
    }
  }
  ${({ isInterlocutorMessage }) =>
    isInterlocutorMessage &&
    css`
      color: ${({ theme }) => theme.colors.ebonyClay.hex()};
      background-color: ${({ theme }) => theme.colors.athensGray.hex()};
      a {
        color: ${({ theme }) => theme.colors.ebonyClay.hex()};
      }
    `}
  ${({ isInterlocutorMessage, $sendStatus, theme }) =>
    !isInterlocutorMessage &&
    $sendStatus === 'failed' &&
    css`
      background-color: ${theme.colors.athensGray.hex()};
      color: ${theme.colors.fadedRed.hex()};
      a {
        color: inherit;
      }
    `}
  ${({ isInterlocutorMessage, $joinedAbove, $joinedBelow }) =>
    isInterlocutorMessage
      ? css`
          border-top-left-radius: ${$joinedAbove ? '4px' : '18px'};
          border-bottom-left-radius: ${$joinedBelow ? '4px' : '18px'};
        `
      : css`
          border-top-right-radius: ${$joinedAbove ? '4px' : '18px'};
          border-bottom-right-radius: ${$joinedBelow ? '4px' : '18px'};
        `}
`

const DeliveryStatus = styled.span`
  position: absolute;
  right: -24px;
  bottom: 2px;
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: ${({ theme, $failed }) =>
    $failed ? theme.colors.fadedRed.hex() : theme.colors.corduroy.hex()};
`

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex: 0 0 20px;
  width: 20px;
  min-width: 0;
  max-width: 20px;
  height: 20px;
  margin: 0;
  border: 0;
  border-radius: 50%;
  padding: 2px;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  &:hover {
    background-color: ${({ theme }) => theme.colors.fadedRed.alpha(0.1).css()};
  }
  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: -2px;
  }
`

const JumpToNewMessagesButton = styled.button`
  position: absolute;
  bottom: ${({ $hasEndOfChat }) => ($hasEndOfChat ? '48px' : '12px')};
  right: 20px;
  border: 0;
  border-radius: 16px;
  padding: 6px 12px;
  background: ${({ theme }) => theme.colors.easternBlue.hex()};
  color: white;
  font-size: 11px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 22;
`

const SystemMessage = ({ className, message }) => (
  <div className={className}>
    <span>{message}</span>
  </div>
)

const SystemMessageStyled = styled(SystemMessage)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 12px;
  &:before {
    content: '';
    width: calc(100% + 2.4rem);
    height: 1px;
    box-sizing: border-box;
    background-color: ${({ theme }) => theme.colors.iceberg.hex()};
    position: absolute;
    top: 50%;
    left: -1.2rem;
    z-index: 1;
  }
  span {
    display: flex;
    position: relative;
    width: auto;
    max-width: calc(100% - 2.4rem);
    border-radius: 19px;
    padding: 7px 16px;
    font-size: 12px;
    background-color: ${({ theme }) => theme.colors.iceberg.hex()};
    box-sizing: border-box;
    color: ${({ theme }) => theme.colors.bluewood.hex()};
    border: solid 1px rgba(19, 162, 191, 0.15);
    text-align: center;
    z-index: 10;
  }
`

const EndOfChatMessage = ({ className, message }) => (
  <div className={className}>
    <span>{message}</span>
  </div>
)

const EndOfChat = styled(EndOfChatMessage)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 1.2rem;
  right: 1.2rem;
  bottom: calc(0.8rem + 4px);
  box-sizing: border-box;
  background: transparent;
  pointer-events: none;
  z-index: 20;
  &:before {
    content: '';
    width: calc(100% + 2.4rem);
    height: 1px;
    box-sizing: border-box;
    background-color: ${({ theme }) => theme.colors.hawkesBlue.hex()};
    position: absolute;
    top: 50%;
    left: -1.2rem;
    z-index: 1;
  }
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: -1.2rem;
    width: calc(100% + 2.4rem);
    height: calc(50% + 0.8rem + 4px);
    background-color: ${({ theme }) => theme.colors.white.hex()};
    z-index: 0;
  }
  span {
    display: flex;
    position: relative;
    width: auto;
    max-width: calc(100% - 2.4rem);
    height: 30px;
    align-items: center;
    white-space: nowrap;
    border-radius: 19px;
    padding: 7px 16px;
    font-size: 12px;
    background-color: ${({ theme }) => theme.colors.hawkesBlue.hex()};
    box-sizing: border-box;
    color: ${({ theme }) => theme.colors.bluewood.hex()};
    border: solid 1px rgba(19, 162, 191, 0.15);
    text-align: center;
    z-index: 10;
  }
`

const Timestamp = styled.span`
  display: inline-block;
  width: 100%;
  text-align: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black.alpha(0.7).hex()};
  margin: 0.4rem 0;
  font-weight: 600;
`

const NewMessagesDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin: 8px 0 12px;
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  font-size: 11px;
  font-weight: 700;
  text-align: center;

  &:before,
  &:after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: ${({ theme }) =>
      theme.colors.easternBlue.alpha(0.35).hex()};
  }

  span {
    white-space: nowrap;
  }
`
